import { Cron } from "croner";
import { isReservedRoute } from "./reservedRoutes";
import {
  isAllowedNonStepPath,
  stepDirFromPath,
  tryParseStepConfig,
} from "./stepConfig";

type ImportedStepApp = {
  stepDir: string;
  name: string;
  type: string;
};

export type PrepareResult =
  | {
      ok: true;
      files: Record<string, string>;
      derivedName: string;
      apps: ImportedStepApp[];
    }
  | { ok: false; error: string };

export type PrepareOptions = {
  maxFiles?: number;
  maxSteps?: number;
  maxFileBytes?: number;
  maxNameLength?: number;
};

const DEFAULT_MAX_FILES = 1000;
const DEFAULT_MAX_STEPS = 100;
const DEFAULT_MAX_FILE_BYTES = 1 * 1024 * 1024;
const DEFAULT_MAX_NAME_LENGTH = 100;
const FALLBACK_NAME = "Imported workflow";

function isImportablePath(path: string): boolean {
  if (isAllowedNonStepPath(path)) return true;
  const dir = stepDirFromPath(path);
  return dir !== null && !dir.startsWith(".");
}

function isValidCron(expr: string): boolean {
  try {
    new Cron(expr);
    return true;
  } catch {
    return false;
  }
}

export function prepareWorkflowImport(
  raw: Record<string, string>,
  fileName: string,
  opts: PrepareOptions = {}
): PrepareResult {
  const maxFiles = opts.maxFiles ?? DEFAULT_MAX_FILES;
  const maxSteps = opts.maxSteps ?? DEFAULT_MAX_STEPS;
  const maxFileBytes = opts.maxFileBytes ?? DEFAULT_MAX_FILE_BYTES;
  const maxNameLength = opts.maxNameLength ?? DEFAULT_MAX_NAME_LENGTH;

  const allKeys = Object.keys(raw);
  if (allKeys.length === 0) return { ok: false, error: "ZIP is empty" };

  const firstSegments = new Set(allKeys.map((k) => k.split("/")[0]));
  const candidate = firstSegments.size === 1 ? [...firstSegments][0] : null;
  // Treat a single top-level folder as a wrapper only when it doesn't itself
  // contain a config.toml — otherwise it's a step dir and stripping it would
  // erase the step.
  const stripPrefix =
    candidate && raw[`${candidate}/config.toml`] === undefined
      ? `${candidate}/`
      : "";

  const files: Record<string, string> = {};
  for (const [path, content] of Object.entries(raw)) {
    const afterPrefix =
      stripPrefix && path.startsWith(stripPrefix)
        ? path.slice(stripPrefix.length)
        : path;
    // Older exports produced `<safeName>//<rest>` paths; keep accepting them.
    const normalized = afterPrefix.replace(/^\/+/, "");
    if (!isImportablePath(normalized)) continue;
    files[normalized] = content;
  }

  const fileCount = Object.keys(files).length;
  if (fileCount > maxFiles) {
    return {
      ok: false,
      error: `ZIP has ${fileCount} files (max ${maxFiles})`,
    };
  }

  for (const [path, content] of Object.entries(files)) {
    const bytes = new Blob([content]).size;
    if (bytes > maxFileBytes) {
      return {
        ok: false,
        error: `File "${path}" is ${bytes} bytes (max ${maxFileBytes})`,
      };
    }
  }

  const stepDirs = new Set<string>();
  for (const p of Object.keys(files)) {
    const dir = stepDirFromPath(p);
    if (dir) stepDirs.add(dir);
  }
  if (stepDirs.size === 0) {
    return { ok: false, error: "ZIP is not a workflow (no steps found)" };
  }
  if (stepDirs.size > maxSteps) {
    return {
      ok: false,
      error: `ZIP has ${stepDirs.size} steps (max ${maxSteps})`,
    };
  }

  const seenRoutes = new Map<string, string>();
  const apps: ImportedStepApp[] = [];
  for (const dir of stepDirs) {
    const cfgRaw = files[`${dir}/config.toml`];
    if (!cfgRaw) continue;
    const cfg = tryParseStepConfig(cfgRaw);
    if (!cfg) {
      return {
        ok: false,
        error: `Step "${dir}" has an unreadable config.toml`,
      };
    }
    if (cfg.cron && !isValidCron(cfg.cron)) {
      return {
        ok: false,
        error: `Step "${dir}" has invalid cron "${cfg.cron}"`,
      };
    }
    for (const app of cfg.connectors ?? []) {
      apps.push({ stepDir: dir, name: app.name, type: app.type });
    }
    if (!cfg.route) continue;
    if (isReservedRoute(cfg.route)) {
      return {
        ok: false,
        error: `Step "${dir}" uses reserved link "${cfg.route}"`,
      };
    }
    const owner = seenRoutes.get(cfg.route);
    if (owner) {
      return {
        ok: false,
        error: `Steps "${owner}" and "${dir}" both claim link "${cfg.route}"`,
      };
    }
    seenRoutes.set(cfg.route, dir);
  }

  const candidateName =
    stripPrefix?.replace(/\/$/, "").trim() ||
    fileName.replace(/\.zip$/i, "").trim();
  const derivedName = (candidateName || FALLBACK_NAME).slice(0, maxNameLength);

  return { ok: true, files, derivedName, apps };
}
